import { useState, useEffect } from 'react'
import { Card, Form, Input, Button, message, Divider, Avatar, Modal, Tabs, Upload, Row, Col, Radio } from 'antd'
import { UserOutlined, LockOutlined, SaveOutlined, CameraOutlined, UploadOutlined, MailOutlined } from '@ant-design/icons'
import { useAuthStore } from '../store/authStore'
import { authApi, operatorsApi, restoreToken } from '../services/api'
import { roleLabel } from '../constants/roles'

// Текст ошибки сервера. Ошибка проверки полей приходит списком - тогда общий текст.
function errorText(error: any, fallback: string): string {
    const detail = error?.response?.data?.detail
    return typeof detail === 'string' ? detail : fallback
}

export default function AccountSettings() {
    const { user, fetchUser } = useAuthStore()
    const [profileForm] = Form.useForm()
    const [passwordForm] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [passwordLoading, setPasswordLoading] = useState(false)


    const [avatarModalOpen, setAvatarModalOpen] = useState(false)
    const [avatarLoading, setAvatarLoading] = useState(false)
    const [generatedSeeds, setGeneratedSeeds] = useState<string[]>([])
    const [avatarStyle, setAvatarStyle] = useState('micah')

    useEffect(() => {
        if (user) {
            profileForm.setFieldsValue({
                username: user.username,
                full_name: user.full_name,
                wiseway_email: user.wiseway_email || '',
                current_password: '',
            })
        }
    }, [user])

    useEffect(() => {

        if (generatedSeeds.length === 0) {
            const seeds = Array.from({ length: 24 }, () => Math.random().toString(36).substring(7))
            setGeneratedSeeds(seeds)
        }
    }, [])

    const handleProfileUpdate = async (values: any) => {
        setLoading(true)
        try {
            const emailChanged = (values.wiseway_email || '').trim().toLowerCase() !== (user?.wiseway_email || '').trim().toLowerCase()
            await operatorsApi.updateProfile({
                username: values.username,
                full_name: values.full_name || '',
                ...(emailChanged ? { wiseway_email: (values.wiseway_email || '').trim(), current_password: values.current_password } : {}),
            })
            await fetchUser()
            profileForm.setFieldValue('current_password', '')
            message.success('Профиль обновлён')
        } catch (error: any) {
            message.error(errorText(error, 'Ошибка обновления профиля'))
        } finally {
            setLoading(false)
        }
    }

    const handlePasswordChange = async (values: any) => {
        if (values.new_password !== values.confirm_password) {
            message.error('Пароли не совпадают')
            return
        }
        setPasswordLoading(true)
        try {
            const { data } = await authApi.changePassword(values.current_password, values.new_password)
            // Смена пароля закрывает все прежние входы, включая этот. Сервер
            // сразу выдал новый токен - продолжаем с ним, без повторного входа
            // (и без кода из письма у тех, у кого он включён).
            restoreToken(data.access_token, user)
            useAuthStore.setState({ token: data.access_token })
            message.success('Пароль изменён. На других устройствах нужно войти заново')
            passwordForm.resetFields()
        } catch (error: any) {
            message.error(errorText(error, 'Ошибка смены пароля'))
        } finally {
            setPasswordLoading(false)
        }
    }

    const handleAvatarUpload = async (file: File) => {
        setAvatarLoading(true)
        try {

            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
            if (!isJpgOrPng) {
                message.error('Можно загружать только JPG/PNG/WEBP файлы!');
                setAvatarLoading(false);
                return false;
            }
            const isLt2M = file.size / 1024 / 1024 < 2;
            if (!isLt2M) {
                message.error('Изображение должно быть меньше 2MB!');
                setAvatarLoading(false);
                return false;
            }

            await operatorsApi.uploadAvatar(file)
            await fetchUser()
            message.success('Аватар обновлён')
            setAvatarModalOpen(false)
        } catch (error: any) {
            message.error('Ошибка загрузки аватара')
        } finally {
            setAvatarLoading(false)
        }
        return false
    }

    const handlePresetSelect = async (seed: string) => {
        setAvatarLoading(true)
        try {
            const avatarUrl = `https://api.dicebear.com/9.x/${avatarStyle}/svg?seed=${seed}`
            await operatorsApi.updateProfile({ avatar_url: avatarUrl })
            await fetchUser()
            message.success('Аватар выбран')
            setAvatarModalOpen(false)
        } catch (error: any) {
            message.error('Ошибка сохранения аватара')
        } finally {
            setAvatarLoading(false)
        }
    }

    const getRoleName = (role: string) => roleLabel(role)

    return (
        <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
            <h1 style={{ color: 'var(--text-primary)', marginBottom: 24 }}>Учётная запись</h1>


            <Card
                style={{
                    background: '#1a1a1a',
                    border: '1px solid #303030',
                    marginBottom: 24,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                    <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setAvatarModalOpen(true)}>
                        <Avatar
                            size={84}
                            src={user?.avatar_url}
                            style={{
                                background: 'linear-gradient(135deg, #1890ff 0%, #0050b3 100%)',
                                border: '2px solid #303030',
                                fontSize: 32
                            }}
                            icon={<UserOutlined />}
                        >
                            {!user?.avatar_url && (user?.full_name || user?.username || 'U')[0].toUpperCase()}
                        </Avatar>
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            background: '#1890ff',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid #1a1a1a',
                            color: 'white'
                        }}>
                            <CameraOutlined style={{ fontSize: 14 }} />
                        </div>
                    </div>

                    <div style={{ marginLeft: 20 }}>
                        <div style={{ fontSize: 24, fontWeight: 600, color: 'white' }}>
                            {user?.full_name || user?.username}
                        </div>
                        <div style={{ color: '#888', marginTop: 4 }}>
                            {getRoleName(user?.role || 'operator')}
                        </div>
                    </div>
                </div>

                <Divider style={{ borderColor: '#303030' }} />

                <Form
                    form={profileForm}
                    layout="vertical"
                    onFinish={handleProfileUpdate}
                >
                    <Form.Item
                        name="username"
                        label={<span style={{ color: '#888' }}>Логин</span>}
                        rules={[
                            { required: true, message: 'Введите логин' },
                            { min: 3, message: 'Минимум 3 символа' },
                        ]}
                    >
                        <Input prefix={<UserOutlined />} placeholder="Введите логин" />
                    </Form.Item>

                    <Form.Item
                        name="full_name"
                        label={<span style={{ color: '#888' }}>Полное имя</span>}
                    >
                        <Input placeholder="Введите ваше имя" />
                    </Form.Item>

                    <Form.Item
                        name="wiseway_email"
                        label={<span style={{ color: '#888' }}>Личная почта для OTP</span>}
                        extra="На неё приходят коды входа. Сменить можно только с текущим паролем."
                        rules={[{ type: 'email', message: 'Почта указана неверно' }]}
                    >
                        <Input prefix={<MailOutlined />} placeholder="name@example.com" />
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.wiseway_email !== next.wiseway_email}>
                        {({ getFieldValue }) => {
                            const changed = (getFieldValue('wiseway_email') || '').trim().toLowerCase() !== (user?.wiseway_email || '').trim().toLowerCase()
                            return changed ? (
                                <Form.Item
                                    name="current_password"
                                    label={<span style={{ color: '#888' }}>Текущий пароль</span>}
                                    rules={[{ required: true, message: 'Чтобы сменить почту, введите текущий пароль' }]}
                                >
                                    <Input.Password prefix={<LockOutlined />} placeholder="Текущий пароль" autoComplete="current-password" />
                                </Form.Item>
                            ) : null
                        }}
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<SaveOutlined />}
                            loading={loading}
                        >
                            Сохранить
                        </Button>
                    </Form.Item>
                </Form>
            </Card>


            <Card
                title={<span style={{ color: 'white' }}>Смена пароля</span>}
                style={{
                    background: '#1a1a1a',
                    border: '1px solid #303030',
                }}
            >
                <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={handlePasswordChange}
                >
                    <Form.Item
                        name="current_password"
                        label={<span style={{ color: '#888' }}>Текущий пароль</span>}
                        rules={[{ required: true, message: 'Введите текущий пароль' }]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Текущий пароль" autoComplete="current-password" />
                    </Form.Item>

                    <Form.Item
                        name="new_password"
                        label={<span style={{ color: '#888' }}>Новый пароль</span>}
                        rules={[
                            { required: true, message: 'Введите новый пароль' },
                            { min: 6, message: 'Минимум 6 символов' },
                        ]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Новый пароль" />
                    </Form.Item>

                    <Form.Item
                        name="confirm_password"
                        label={<span style={{ color: '#888' }}>Подтвердите пароль</span>}
                        rules={[{ required: true, message: 'Подтвердите пароль' }]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Повторите пароль" />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<LockOutlined />}
                            loading={passwordLoading}
                        >
                            Изменить пароль
                        </Button>
                    </Form.Item>
                </Form>
            </Card>



            <Modal
                title="Изменить аватар"
                open={avatarModalOpen}
                onCancel={() => setAvatarModalOpen(false)}
                footer={null}
                width={600}
                centered
            >
                <Tabs
                    items={[
                        {
                            key: 'presets',
                            label: 'Выбрать из набора',
                            children: (
                                <div style={{ padding: '16px 0' }}>
                                    <div style={{ marginBottom: 24, textAlign: 'center' }}>
                                        <Radio.Group
                                            value={avatarStyle}
                                            onChange={(e) => setAvatarStyle(e.target.value)}
                                            buttonStyle="solid"
                                        >
                                            <Radio.Button value="micah">Современные</Radio.Button>
                                            <Radio.Button value="avataaars">Классика</Radio.Button>
                                            <Radio.Button value="bottts">Роботы</Radio.Button>
                                            <Radio.Button value="initials">Буквы</Radio.Button>
                                        </Radio.Group>
                                    </div>

                                    <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: 8 }}>
                                        <Row gutter={[16, 16]}>
                                            {generatedSeeds.map(seed => (
                                                <Col span={6} key={seed} style={{ display: 'flex', justifyContent: 'center' }}>
                                                    <div
                                                        style={{
                                                            cursor: 'pointer',
                                                            border: '2px solid transparent',
                                                            borderRadius: '50%',
                                                            padding: 2,
                                                            transition: 'all 0.2s'
                                                        }}
                                                        className="avatar-preset"
                                                        onClick={() => handlePresetSelect(seed)}
                                                        onMouseEnter={(e) => e.currentTarget.style.borderColor = '#1890ff'}
                                                        onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                                                    >
                                                        <Avatar
                                                            size={80}
                                                            src={`https://api.dicebear.com/9.x/${avatarStyle}/svg?seed=${seed}`}
                                                        />
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>

                                    <div style={{ marginTop: 16, textAlign: 'center' }}>
                                        <Button onClick={() => setGeneratedSeeds(Array.from({ length: 24 }, () => Math.random().toString(36).substring(7)))}>
                                            Показать другие
                                        </Button>
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: 'upload',
                            label: 'Загрузить своё',
                            children: (
                                <div style={{
                                    padding: '40px 0',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Upload
                                        showUploadList={false}
                                        beforeUpload={handleAvatarUpload}
                                        accept="image/png,image/jpeg,image/webp"
                                    >
                                        <Button
                                            size="large"
                                            icon={<UploadOutlined />}
                                            loading={avatarLoading}
                                            type="primary"
                                        >
                                            Выберите файл
                                        </Button>
                                    </Upload>
                                    <div style={{ marginTop: 16, color: '#888', textAlign: 'center' }}>
                                        Поддерживаются JPG, PNG, WEBP.<br />
                                        Максимальный размер: 2 MB.
                                    </div>
                                </div>
                            )
                        }
                    ]}
                />
            </Modal>
        </div>
    )
}
